import React, { useContext, useState } from 'react';
import { useI18nContext, Trans } from '@ecg-marktplaats/js-react-i18n';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import {
  Tabs,
  Tab,
  TabPanel,
  Divider,
  Icon,
  Pill,
  Link,
  StarRating,
  TextSmall,
  Card,
  Title4,
  Title5,
} from '@hz-design-system/web-ui';
import Classes from './UserReviewsComponent.scss';

const WrapperConponent = ({ isMyReviews, children }: { isMyReviews: boolean; children: React.ReactNode }) =>
  isMyReviews ? (
    <Card padding="s" className="u-marginTopM">
      {children}
    </Card>
  ) : (
    <>{children}</>
  );

const UserReviewsComponent = () => {
  const { reviewee, summary, reviews, isMyReviews = false } = useContext(EnvironmentContext);
  const { t, date } = useI18nContext();
  const [activeTab, setActiveTab] = useState(0);
  const [isPositiveOpen, setIsPositiveOpen] = useState(false);
  const [isNegativeOpen, setIsNegativeOpen] = useState(false);
  const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<number[]>([]);
  const isCharacterisicsOpen = (id: number) => selectedCharacteristicIds.includes(id);

  const togglePositive = () => {
    setIsPositiveOpen(!isPositiveOpen);
  };

  const toggleNegative = () => {
    setIsNegativeOpen(!isNegativeOpen);
  };

  const toggleCharacteristic = (id: number) => {
    if (selectedCharacteristicIds.includes(id)) {
      setSelectedCharacteristicIds(selectedCharacteristicIds.filter((i) => i !== id));
    } else {
      setSelectedCharacteristicIds([...selectedCharacteristicIds, id]);
    }
  };

  return (
    <WrapperConponent isMyReviews={isMyReviews}>
      <div className={Classes.root}>
        <Title4>{reviewee.nickname}</Title4>
        <StarRating
          label={`${summary.numberOfReviews} ${t('userReviews.review_plural')}`}
          rating={summary.averageScore}
        />
        <Divider />
        {summary.scores.map(({ key, percent, count }: { key: any; percent: number; count: number }) => (
          <div key={key} className={Classes.progressBar}>
            <div>
              <Pill
                className={Classes.pill}
                translations={
                  {
                    ariaLabelActive: key,
                    ariaLabelDefault: key,
                  } as const
                }
              >
                {key}
                <Icon name="saved" color="signal-success-default" />
              </Pill>
            </div>
            <div className={Classes.progress}>
              <div className={Classes.bar} style={{ width: `${percent}%` }} />
            </div>
            <div className={Classes.count}>{count}</div>
          </div>
        ))}
        <Divider />
        <Tabs className={Classes.tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
          <Tab
            className={Classes.tab}
            label={`${t('userReviews.characteristics.positive.title')} (${summary?.characteristics?.positive?.length})`}
          />
          <Tab
            className={Classes.tab}
            label={`${t('userReviews.characteristics.negative.title')} (${summary?.characteristics?.negative?.length})`}
          />
        </Tabs>
        <TabPanel activeTab={activeTab} tab={0}>
          <Trans
            className={Classes.tabSubtitle}
            i18nKey="userReviews.characteristics.positive.body"
            nickname={reviewee?.nickname}
          />
          {!isPositiveOpen
            ? summary.characteristics.positive.slice(0, 4).map((characteristic: any) => (
                <Pill
                  key={characteristic.text}
                  className={Classes.characteristicPill}
                  translations={
                    {
                      ariaLabelActive: characteristic.text,
                      ariaLabelDefault: characteristic.text,
                    } as const
                  }
                >
                  {characteristic.text}
                  <div className={Classes.characteristicPillCount}>{characteristic.count}</div>
                </Pill>
              ))
            : summary.characteristics.positive.map((characteristic: any) => (
                <Pill
                  key={characteristic.text}
                  className={Classes.characteristicPill}
                  translations={
                    {
                      ariaLabelActive: characteristic.text,
                      ariaLabelDefault: characteristic.text,
                    } as const
                  }
                >
                  {characteristic.text}
                  <div className={Classes.characteristicPillCount}>{characteristic.count}</div>
                </Pill>
              ))}
          {summary.characteristics.positive.length > 4 && (
            <Link onClick={togglePositive}>
              {!isPositiveOpen && (
                <>
                  {t('userReviews.more')} <Icon name="arrow-down" />
                </>
              )}
              {isPositiveOpen && (
                <>
                  {t('userReviews.less')} <Icon name="arrow-up" />
                </>
              )}
            </Link>
          )}
        </TabPanel>
        <TabPanel activeTab={activeTab} tab={1}>
          <Trans className={Classes.tabSubtitle} i18nKey="userReviews.characteristics.negative.body" />
          {!isNegativeOpen
            ? summary.characteristics.negative.slice(0, 4).map((characteristic: any) => (
                <Pill
                  key={characteristic.text}
                  className={Classes.characteristicNegativePill}
                  translations={
                    {
                      ariaLabelActive: characteristic.text,
                      ariaLabelDefault: characteristic.text,
                    } as const
                  }
                >
                  {characteristic.text}
                  <div className={Classes.characteristicNegativePillCount}>{characteristic.count}</div>
                </Pill>
              ))
            : summary.characteristics.negative.map((characteristic: any) => (
                <Pill
                  key={characteristic.text}
                  className={Classes.characteristicNegativePill}
                  translations={
                    {
                      ariaLabelActive: characteristic.text,
                      ariaLabelDefault: characteristic.text,
                    } as const
                  }
                >
                  {characteristic.text}
                  <div className={Classes.characteristicNegativePillCount}>{characteristic.count}</div>
                </Pill>
              ))}
          {summary.characteristics.negative.length > 4 && (
            <Link onClick={toggleNegative}>
              {!isNegativeOpen && (
                <>
                  {t('userReviews.more')} <Icon name="arrow-down" />
                </>
              )}
              {isNegativeOpen && (
                <>
                  {t('userReviews.less')} <Icon name="arrow-up" />
                </>
              )}
            </Link>
          )}
        </TabPanel>
        <div className={Classes.reviewsByOthersHeader}>
          <Title4>
            {t('userReviews.reviewsByOthers')} ({reviews.length})
          </Title4>
        </div>
        {reviews.map((review: any) => (
          <div key={review.id} className={Classes.review}>
            <div className={Classes.reviewHeader}>
              <Title5>{review.reviewer.nickname}</Title5>
              <TextSmall className={Classes.reviewDate}>{date(new Date(review.creationDate), 'd MMM `yy')}</TextSmall>
            </div>
            <TextSmall className={Classes.reviewCategory}>{review.advertisement.categoryName}</TextSmall>
            <StarRating rating={review.score} />
            <div className={Classes.reviewPills}>
              {!isCharacterisicsOpen(review.id)
                ? review.characteristics.slice(0, 4).map((characteristic: any) => {
                    if (characteristic.isPositive) {
                      return (
                        <Pill
                          key={characteristic.text}
                          className={Classes.characteristicPill}
                          translations={
                            {
                              ariaLabelActive: characteristic.text,
                              ariaLabelDefault: characteristic.text,
                            } as const
                          }
                        >
                          {characteristic.text}
                        </Pill>
                      );
                    }
                    return (
                      <Pill
                        key={characteristic.text}
                        className={Classes.characteristicNegativePill}
                        translations={
                          {
                            ariaLabelActive: characteristic.text,
                            ariaLabelDefault: characteristic.text,
                          } as const
                        }
                      >
                        {characteristic.text}
                      </Pill>
                    );
                  })
                : review.characteristics.map((characteristic: any) => {
                    if (characteristic.isPositive) {
                      return (
                        <Pill
                          key={characteristic.text}
                          className={Classes.characteristicPill}
                          translations={
                            {
                              ariaLabelActive: characteristic.text,
                              ariaLabelDefault: characteristic.text,
                            } as const
                          }
                        >
                          {characteristic.text}
                        </Pill>
                      );
                    }
                    return (
                      <Pill
                        key={characteristic.text}
                        className={Classes.characteristicNegativePill}
                        translations={
                          {
                            ariaLabelActive: characteristic.text,
                            ariaLabelDefault: characteristic.text,
                          } as const
                        }
                      >
                        {characteristic.text}
                      </Pill>
                    );
                  })}
              {review.characteristics.length > 4 && (
                <Link onClick={() => toggleCharacteristic(review.id)}>
                  {!isCharacterisicsOpen(review.id) && (
                    <>
                      {t('userReviews.more')} <Icon name="arrow-down" />
                    </>
                  )}
                  {isCharacterisicsOpen(review.id) && (
                    <>
                      {t('userReviews.less')} <Icon name="arrow-up" />
                    </>
                  )}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </WrapperConponent>
  );
};

export default UserReviewsComponent;
